import React, { useEffect, useMemo, useState } from 'react';
import { gql } from '@apollo/client';
import { useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import FullPageLoader from '../../components/common/FullPageLoader';
import { ProductionStatus, AllOrdersView } from '../../features/production';

const PRODUCTION_RECORD_FRAGMENT = `
  orderId
  orderNo
  customerName
  supplier
  status
  expectedCompletionDate
  lastUpdated
  updatedBy
  notes
  history {
    id
    statusChange
    timestamp
    changedBy
    notes
  }
  attachments {
    id
    fileUrl
    fileName
    fileType
    uploadedDate
    uploadedBy
  }
`;

const PRODUCTION_ORDERS_QUERY = gql`
  query ProductionOrders($limit: Int!, $offset: Int!) {
    productionOrders(limit: $limit, offset: $offset) {
      ${PRODUCTION_RECORD_FRAGMENT}
    }
  }
`;

const UPDATE_PRODUCTION_STATUS_MUTATION = gql`
  mutation UpdateProductionStatus($input: UpdateOrderProductionStatusInput!) {
    updateProductionStatus(input: $input) {
      ${PRODUCTION_RECORD_FRAGMENT}
    }
  }
`;

type ProductionStatusType = 'not-started' | 'in-production' | 'qc-finishing' | 'delayed' | 'ready-for-dispatch';

interface ProductionRecord {
  orderId: number;
  orderNo: string;
  customerName: string;
  supplier: string;
  status: ProductionStatusType;
  expectedCompletionDate?: string | null;
  lastUpdated: string;
  updatedBy: string;
  notes?: string | null;
  history: Array<{
    id: number;
    statusChange: ProductionStatusType;
    timestamp: string;
    changedBy: string;
    notes?: string | null;
  }>;
  attachments: Array<{
    id: number;
    fileUrl: string;
    fileName: string;
    fileType?: string | null;
    uploadedDate?: string | null;
    uploadedBy?: string | null;
  }>;
}

interface ProductionOrdersQueryData {
  productionOrders: ProductionRecord[];
}

interface UpdateProductionStatusMutationData {
  updateProductionStatus: ProductionRecord;
}

export const ProductionStatusPage: React.FC = () => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [records, setRecords] = useState<ProductionRecord[]>([]);
  const { data, loading, refetch } = useGraphQLQuery<ProductionOrdersQueryData>(PRODUCTION_ORDERS_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
  });
  const [updateProductionStatus] = useGraphQLMutation<UpdateProductionStatusMutationData>(UPDATE_PRODUCTION_STATUS_MUTATION, {
    showErrorToast: false,
    showSuccessToast: false,
  });

  useEffect(() => {
    if (data?.productionOrders) {
      setRecords(data.productionOrders);
    }
  }, [data]);

  const orderByOrderNo = useMemo(() => {
    const entries = records.map((record) => [record.orderNo, record] as const);
    return new Map(entries);
  }, [records]);

  const orders = useMemo(
    () => records.map((record) => ({
      id: record.orderNo,
      customer: record.customerName,
      supplier: record.supplier,
      status: record.status,
      expectedCompletionDate: record.expectedCompletionDate || '',
      backendOrderId: record.orderId,
      notes: record.notes || '',
      lastUpdated: record.lastUpdated,
      updatedBy: record.updatedBy,
      history: record.history,
      attachments: record.attachments,
    })),
    [records],
  );
  const selectedRecord = orders.find((record) => record.id === selectedOrderId);

  if (loading && orders.length === 0) {
    return <FullPageLoader />;
  }

  if (selectedOrderId && selectedRecord) {
    return (
      <ProductionStatus 
        selectedOrderId={selectedOrderId}
        onOrderChange={setSelectedOrderId}
        onBack={() => setSelectedOrderId(null)}
        orders={orders}
        onSaveProductionUpdate={async (payload) => {
          const orderNo = selectedOrderId;
          const orderToUpdate = orderNo ? orderByOrderNo.get(orderNo) : undefined;
          if (!orderToUpdate) {
            const err = 'Unable to resolve selected order for status update';
            console.error('[Production Save]', err);
            throw new Error(err);
          }

          console.log('[Production Save] Starting with payload:', { 
            orderNo,
            orderId: orderToUpdate.orderId,
            status: payload.status,
            expectedCompletionDate: payload.expectedCompletionDate,
            notes: payload.notes,
          });

          let response;
          try {
            response = await updateProductionStatus({
              variables: {
                input: {
                  orderId: orderToUpdate.orderId,
                  status: payload.status,
                  expectedCompletionDate: payload.expectedCompletionDate || '',
                  notes: payload.notes || '',
                  actor: payload.actor,
                },
              },
            });
            console.log('[Production Save] Mutation response status:', response.data?.updateProductionStatus?.status);

            if (response.errors && response.errors.length > 0) {
              const errorMsg = response.errors[0].message || 'Unknown GraphQL error';
              console.error('[Production Save] GraphQL error:', errorMsg);
              throw new Error(errorMsg);
            }
          } catch (mutationErr) {
            console.error('[Production Save] Mutation failed:', mutationErr);
            throw mutationErr;
          }

          const updated = response.data?.updateProductionStatus;
          if (updated?.orderId) {
            console.log('[Production Save] Updating local records with status:', updated.status);
            setRecords((prev) => prev.map((record) => (
              record.orderId === updated.orderId ? updated : record
            )));
          } else {
            console.warn('[Production Save] No updated record in mutation response');
          }

          console.log('[Production Save] Calling refetch...');
          try {
            const refreshed = await refetch({ limit: 300, offset: 0 });
            if (refreshed.data?.productionOrders?.length > 0) {
              const refreshedOrder = refreshed.data.productionOrders.find(r => r.orderId === orderToUpdate.orderId);
              console.log('[Production Save] Refetch complete. Updated order status:', refreshedOrder?.status);
              setRecords(refreshed.data.productionOrders);
            } else {
              console.warn('[Production Save] Refetch returned no data');
            }
          } catch (refetchErr) {
            console.error('[Production Save] Refetch failed:', refetchErr);
            throw refetchErr;
          }
        }}
      />
    );
  }

  return (
    <AllOrdersView 
      orders={orders}
      onSelectOrder={setSelectedOrderId}
    />
  );
};

